/**
 * Lohnbuchungsbeleg — checksum difference analysis.
 *
 * The checksum (`voucher.checksum`) is Σ rowTotal over the template lines flagged
 * `IncludeInChecksum` — mirroring Heike's Excel control sum, which should net to ~0 for a
 * balanced payroll. When it doesn't, this helper explains *where* by:
 *
 *   1. flagging the concrete leaks (unmatched Lohnarten, missing cost centers) that keep
 *      amounts out of the beleg,
 *   2. surfacing cost centers whose gross rows are missing from the raw export (deductions
 *      present but no/too little Brutto — computed server-side, see api compute.ts), and
 *   3. listing the checksum-relevant template rows that actually make up the sum (largest
 *      first) — so a wrongly-flagged row is obvious. In particular it warns when an
 *      employer-contribution row (AG-Beitrag, account 57xxx) is included in the checksum,
 *      since those normally are not (they self-balance Soll/Haben) — a single such row with
 *      IncludeInChecksum=1 is the usual cause of a large residual.
 */

import type { ComputedVoucher, ComputedLine, CostCenterGrossDeficit } from '../types/lohnBuchungsbeleg';

export interface ChecksumLeak {
    key: 'unmatched' | 'missingCostCenters';
    label: string;
    amount: number;
    detail: string;
}

export interface ChecksumRow {
    beschreibung: string;
    soll: string | null;
    haben: string | null;
    rowTotal: number;
    /** True if this looks like an employer-contribution row (Soll account 57xxx) — usually
     *  should NOT be in the checksum, so its presence here is suspicious. */
    suspectAgBeitrag: boolean;
}

export interface ChecksumAnalysis {
    checksum: number;
    balanced: boolean;
    leaks: ChecksumLeak[];         // amounts kept out of the beleg (actionable via panels)
    rows: ChecksumRow[];           // checksum-relevant rows making up the sum, largest first
    suspects: ChecksumRow[];       // rows likely wrongly flagged into the checksum
    grossDeficits: CostCenterGrossDeficit[]; // KSTs with deductions but missing gross rows
}

function round(n: number): number {
    return Math.round(n * 100) / 100;
}

/** An AG-Beitrag (employer contribution) row: Soll account in the 57xx range. */
function isAgBeitrag(line: ComputedLine): boolean {
    const soll = (line.soll ?? '').trim();
    return /^57\d/.test(soll);
}

export function analyzeChecksum(voucher: ComputedVoucher): ChecksumAnalysis {
    const leaks: ChecksumLeak[] = [];
    const unmatchedSum = round(voucher.unmatchedTotal ?? 0);
    const missingCcSum = round(voucher.unmatchedCostCenterTotal ?? 0);
    if (Math.abs(unmatchedSum) >= 0.005) {
        leaks.push({
            key: 'unmatched',
            label: `Nicht zugeordnete Lohnarten (${voucher.unmatched?.length ?? 0})`,
            amount: unmatchedSum,
            detail: 'Fehlen im Beleg — je Lohnart eine Vorlage-Zeile anlegen (Panel unten).',
        });
    }
    if (Math.abs(missingCcSum) >= 0.005) {
        leaks.push({
            key: 'missingCostCenters',
            label: `Fehlende Kostenstellen (${voucher.unmatchedCostCenters?.length ?? 0})`,
            amount: missingCcSum,
            detail: 'Beträge auf nicht vorhandenen KST-Spalten fallen weg — Kostenstelle anlegen (Panel unten).',
        });
    }

    const rows: ChecksumRow[] = voucher.lines
        .filter(l => l.includeInChecksum && Math.abs(l.rowTotal) >= 0.005)
        .map(l => ({
            beschreibung: l.beschreibung,
            soll: l.soll,
            haben: l.haben,
            rowTotal: round(l.rowTotal),
            suspectAgBeitrag: isAgBeitrag(l),
        }))
        .sort((a, b) => Math.abs(b.rowTotal) - Math.abs(a.rowTotal));

    const suspects = rows.filter(r => r.suspectAgBeitrag);

    const grossDeficits = (voucher.costCenterGrossDeficits ?? []).map(d => ({
        ...d,
        gross: round(d.gross),
        deductions: round(d.deductions),
    }));

    return {
        checksum: round(voucher.checksum),
        balanced: voucher.balanced,
        leaks,
        rows,
        suspects,
        grossDeficits,
    };
}
